import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiFailure, api, apiRaw, describeError, getTokens, setTokens, type Envelope, type Tokens } from '../api';
import { getDeviceId, useSession, type AuthResult, type Me } from '../session';

/** change 9: GET /api/me/entitlements — count caps + usage + feature gates. */
interface Entitlements {
  vip_tier: 'free' | 'pro';
  features: Record<string, boolean | string>;
  limits: { characters: Count; world_books: Count; personas: Count; ai_character_gen_per_day: Count & { resets_at: string } };
  gates: { custom_presets: boolean; local_backend: boolean; byok: boolean; raw_proxy: boolean };
}
interface Count { limit: number | null; count: number }
const LIMIT_LABELS: Record<keyof Entitlements['limits'], string> = { characters: '角色卡', world_books: '世界书', personas: 'Persona', ai_character_gen_per_day: 'AI 造卡 / 日' };
const GATE_LABELS: Record<keyof Entitlements['gates'], string> = { custom_presets: '预设可编辑（custom_presets）', local_backend: '自有模型 / local 档案（local_backend）', byok: '托管密钥（byok）', raw_proxy: '裸代理（raw_proxy）' };

/** change 10 */
interface Device { id: string; platform: string; push_provider: string | null; push_token_suffix: string | null; timezone: string; locale: string | null; app_version: string | null; model: string | null; last_seen_at: string; created_at: string }
interface Session { sid: string; device_id: string | null; user_agent: string | null; created_at: string; rotated_at: string; expires_at: string; current: boolean }
interface OtpResponse { sent: boolean; already_verified?: boolean; debug_code?: string; expires_in?: number; resend_after?: number }

const fmt = (iso: string) => new Date(iso).toLocaleString();

export function AccountPage() {
  const { me, reloadMe, logout, applyAuthResult } = useSession();
  const [message, setMessage] = useState<string | null>(null);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [entErr, setEntErr] = useState<string | null>(null);
  const tokens = getTokens();

  const report = (err: unknown) => setMessage(err instanceof ApiFailure ? describeError(err) : String(err));

  const loadEntitlements = useCallback(async () => {
    if (!me) return;
    setEntErr(null);
    try {
      setEnt((await api<Entitlements>('/api/me/entitlements')).data);
    } catch (err) {
      setEntErr(describeError(err));
    }
  }, [me]);
  useEffect(() => {
    void loadEntitlements();
  }, [loadEntitlements]);

  const testRefresh = async () => {
    setMessage(null);
    const t = getTokens();
    if (!t) return;
    try {
      const { data } = await api<Tokens>('/api/auth/refresh', { method: 'POST', body: { refresh_token: t.refresh_token, device_id: getDeviceId() }, auth: false });
      setTokens(data);
      setMessage(`刷新成功：refresh token 已轮换（session ${data.session_id}）。再用旧 token 刷新应返回 AUTH_REFRESH_REUSED。`);
    } catch (err) {
      report(err);
    }
  };

  const replayOld = async () => {
    setMessage(null);
    const t = getTokens();
    if (!t) return;
    const old = t.refresh_token;
    try {
      const { data } = await api<Tokens>('/api/auth/refresh', { method: 'POST', body: { refresh_token: old }, auth: false });
      setTokens(data);
      await api('/api/auth/refresh', { method: 'POST', body: { refresh_token: old }, auth: false });
      setMessage('意外：重放旧 token 竟然成功');
    } catch (err) {
      setMessage(err instanceof ApiFailure ? `预期行为：${err.error.code}（会话已撕除，需要重新登录）` : String(err));
      setTokens(null);
      await reloadMe();
    }
  };

  if (!me) return null;
  const pending = me.status === 'pending_deletion';

  return (
    <div className="space-y-6">
      {pending && me.deletion && (
        <section className="card border border-amber-700/60 bg-amber-950/30">
          <h2 className="mb-1 font-semibold text-amber-200">账号处于注销宽限期</h2>
          <p className="text-xs text-amber-100/80">将于 <span className="font-mono">{fmt(me.deletion.purge_after)}</span> 物理删除；期间除 <span className="font-mono">GET /api/me</span>、取消注销、<span className="font-mono">/api/auth/*</span> 外其它接口返回 403 <span className="font-mono">AUTH_ACCOUNT_PENDING_DELETION</span>。</p>
          <button className="btn mt-3" onClick={() => void (async () => { try { const { data } = await api<AuthResult>('/api/me/delete/cancel', { method: 'POST' }); applyAuthResult(data); setMessage('已取消注销，账号恢复 active，令牌已更换'); } catch (err) { report(err); } })()}>取消注销</button>
        </section>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <section className="card">
          <h2 className="mb-3 font-semibold">当前用户 <span className="text-xs text-zinc-400">GET /api/me</span></h2>
          <pre className="json">{JSON.stringify(me, null, 2)}</pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <button className="btn-ghost" onClick={() => void reloadMe()}>重新读取</button>
            <button className="btn-ghost" onClick={() => void testRefresh()}>刷新令牌（轮换）</button>
            <button className="btn-ghost" onClick={() => void replayOld()}>重放旧 refresh（应被拒并撕会话）</button>
            <button className="btn-ghost" onClick={() => void logout(false)}>退出当前设备</button>
            <button className="btn-ghost" onClick={() => void logout(true)}>登出所有设备</button>
          </div>
          {tokens && <p className="mt-3 break-all font-mono text-[10px] text-zinc-500">session {tokens.session_id} · device {getDeviceId()}</p>}
          {message && <p className="mt-3 rounded-lg bg-zinc-950 p-2 text-xs text-zinc-300">{message}</p>}
        </section>

        <section className="card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">权益 <span className="text-xs text-zinc-400">GET /api/me/entitlements</span></h2>
            <button className="btn-ghost" onClick={() => void loadEntitlements()}>刷新</button>
          </div>
          {entErr && <p className="text-sm text-red-300">{entErr}</p>}
          {ent && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-zinc-400">档位 <span className="font-mono text-zinc-200">{ent.vip_tier}</span>（产品显示名 Pro）· 由订阅决定（change 12，钻石页「Pro 订阅」可模拟购买 / 到期）· 上限来自远程配置 <span className="font-mono">plan_limits</span>，<span className="font-mono">null</span> = 无上限；只拦新建 / 导入 / 复制 / 恢复，读改删不受限</div>
              <ul className="space-y-2">
                {(Object.keys(LIMIT_LABELS) as Array<keyof Entitlements['limits']>).map((k) => {
                  const l = ent.limits[k];
                  const pct = l.limit === null || l.limit === 0 ? 0 : Math.min(100, Math.round((l.count / l.limit) * 100));
                  const full = l.limit !== null && l.count >= l.limit;
                  return (
                    <li key={k}>
                      <div className="flex justify-between text-xs"><span>{LIMIT_LABELS[k]}</span><span className={`font-mono ${full ? 'text-amber-300' : 'text-zinc-300'}`}>{l.count} / {l.limit === null ? '∞' : l.limit}{k === 'ai_character_gen_per_day' ? ` · 重置 ${fmt((l as Count & { resets_at: string }).resets_at)}` : ''}</span></div>
                      <div className="mt-1 h-1.5 rounded bg-zinc-800"><div className={`h-1.5 rounded ${full ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${l.limit === null ? 4 : pct}%` }} /></div>
                    </li>
                  );
                })}
              </ul>
              <ul className="grid gap-1 text-xs sm:grid-cols-2">
                {(Object.keys(GATE_LABELS) as Array<keyof Entitlements['gates']>).map((k) => (
                  <li key={k} className="flex items-center gap-2"><span className={`inline-block h-2 w-2 rounded-full ${ent.gates[k] ? 'bg-emerald-400' : 'bg-zinc-600'}`} /><span className={ent.gates[k] ? 'text-zinc-200' : 'text-zinc-500'}>{GATE_LABELS[k]}</span></li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <ProfileCard me={me} onChanged={reloadMe} />
        <SignInMethodsCard me={me} onChanged={reloadMe} />
        <DevicesCard />
        <SessionsCard />
        <DeleteAccountCard me={me} />
      </div>
    </div>
  );
}

function ProfileCard({ me, onChanged }: { me: Me; onChanged: () => Promise<void> }) {
  const [name, setName] = useState(me.name);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => setName(me.name), [me.name]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await api<Me>('/api/me', { method: 'PATCH', body: { name } });
      await onChanged();
      setMsg('昵称已保存');
    } catch (err) {
      setMsg(describeError(err));
    }
  };
  const upload = async (file: File) => {
    setMsg(null);
    try {
      const up = await apiRaw('/api/media/upload?purpose=account', { method: 'POST', body: file, headers: { 'Content-Type': file.type || 'image/png' } });
      const env = (await up.json()) as Envelope<{ key: string }>;
      if (!env.success) throw new ApiFailure(up.status, env.error, up.headers.get('X-Request-Id'));
      await api<Me>('/api/me', { method: 'PATCH', body: { avatar_key: env.data.key } });
      await onChanged();
      setMsg('头像已更新');
    } catch (err) {
      setMsg(describeError(err));
    }
  };
  return (
    <section className="card">
      <h2 className="mb-3 font-semibold">资料 <span className="text-xs text-zinc-400">PATCH /api/me · POST /api/media/upload?purpose=account</span></h2>
      <div className="flex items-start gap-4">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-zinc-800 text-lg font-semibold">
          {me.avatar_url ? <img src={me.avatar_url} alt="" className="h-full w-full object-cover" /> : (me.name || me.display_code).slice(0, 1)}
        </div>
        <form onSubmit={save} className="flex-1 space-y-2">
          <div>
            <label className="label">昵称（1–200 字；空时客户端显示 display_code {me.display_code}）</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn">保存昵称</button>
            <label className="btn-ghost cursor-pointer">
              上传头像<input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
            </label>
            {me.avatar_url && <button type="button" className="btn-ghost" onClick={() => void (async () => { await api('/api/me', { method: 'PATCH', body: { avatar_key: null } }); await onChanged(); })()}>清除头像</button>}
          </div>
          {me.avatar_key && <p className="text-[11px] text-zinc-500">avatar_key <span className="font-mono">{me.avatar_key}</span> · 换头像 / 清除时旧对象由 auth 回收；人设「使用账号头像」用它作 <span className="font-mono">POST /api/media/copy</span> 的 source_key（add-mvp-protocol-gaps-3）</p>}
        </form>
      </div>
      {msg && <p className="mt-3 text-xs text-zinc-300">{msg}</p>}
    </section>
  );
}

function SignInMethodsCard({ me, onChanged }: { me: Me; onChanged: () => Promise<void> }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [code, setCode] = useState('');
  const [codeHint, setCodeHint] = useState<string | null>(null);
  const [idToken, setIdToken] = useState('');
  const [provider, setProvider] = useState<'apple' | 'google'>('google');
  const [msg, setMsg] = useState<string | null>(null);
  const run = (fn: () => Promise<string | void>) => void (async () => { setMsg(null); try { const m = await fn(); await onChanged(); if (m) setMsg(m); } catch (err) { setMsg(describeError(err)); } })();

  return (
    <section className="card">
      <h2 className="mb-3 font-semibold">登录方式 <span className="text-xs text-zinc-400">/api/me/identities · /api/auth/password/* · /api/auth/email/verify</span></h2>
      <ul className="mb-3 space-y-1 text-xs">
        <li className="flex items-center gap-2"><Dot on={me.has_password} />密码：{me.has_password ? '已设置' : '未设置'}</li>
        <li className="flex items-center gap-2"><Dot on={me.email_verified} />邮箱 {me.email}：{me.email_verified ? '已验证（可验证码登录 / 找回密码）' : '未验证'}</li>
        {me.identities.filter((i) => i.provider !== 'email').map((i) => (
          <li key={i.provider} className="flex items-center gap-2"><Dot on />{i.provider}{i.email ? ` · ${i.email}` : ''} · {fmt(i.linked_at)}
            <button className="btn-ghost py-0" onClick={() => run(async () => { await api(`/api/me/identities/${i.provider}`, { method: 'DELETE' }); return `已解绑 ${i.provider}`; })}>解绑</button>
          </li>
        ))}
      </ul>
      {!me.email_verified && (
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <button className="btn-ghost" onClick={() => run(async () => { const { data } = await api<OtpResponse>('/api/auth/email/verify/request', { method: 'POST' }); if (data.debug_code) setCode(data.debug_code); setCodeHint(data.already_verified ? '已验证' : `验证码已发送${data.debug_code ? `（debug_code ${data.debug_code} 已填入）` : ''}`); })}>发送邮箱验证码</button>
          <input className="input w-32 font-mono" placeholder="6 位" value={code} onChange={(e) => setCode(e.target.value)} />
          <button className="btn" onClick={() => run(async () => { await api('/api/auth/email/verify', { method: 'POST', body: { code } }); setCode(''); return '邮箱已验证'; })}>验证</button>
          {codeHint && <span className="text-[11px] text-zinc-400">{codeHint}</span>}
        </div>
      )}
      <form className="mb-3 grid gap-2 md:grid-cols-[1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); run(async () => { if (me.has_password) { const { data } = await api<Tokens>('/api/auth/password/change', { method: 'POST', body: { current_password: current, new_password: next } }); setTokens(data); } else await api('/api/auth/password/set', { method: 'POST', body: { new_password: next } }); setCurrent(''); setNext(''); return me.has_password ? '密码已修改，其他设备已下线' : '密码已设置'; }); }}>
        {me.has_password && <input className="input" type="password" placeholder="当前密码" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />}
        <input className="input" type="password" placeholder={me.has_password ? '新密码（≥ 8 位）' : '设置密码（≥ 8 位）'} value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} autoComplete="new-password" />
        <button className="btn">{me.has_password ? '修改密码' : '设置密码'}</button>
      </form>
      <div className="grid gap-2 md:grid-cols-[110px_1fr_auto]">
        <select className="input" value={provider} onChange={(e) => setProvider(e.target.value as 'apple' | 'google')}><option value="google">google</option><option value="apple">apple</option></select>
        <input className="input font-mono text-[11px]" placeholder="绑定：粘贴 id_token" value={idToken} onChange={(e) => setIdToken(e.target.value)} />
        <button className="btn-ghost" disabled={!idToken} onClick={() => run(async () => { await api(`/api/me/identities/${provider}`, { method: 'POST', body: { id_token: idToken.trim() } }); setIdToken(''); return `已绑定 ${provider}`; })}>绑定</button>
      </div>
      {msg && <p className="mt-3 text-xs text-zinc-300">{msg}</p>}
    </section>
  );
}

function DevicesCard() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => { try { setDevices((await api<Device[]>('/api/me/devices')).data); } catch (err) { setMsg(describeError(err)); } }, []);
  useEffect(() => { void load(); }, [load]);
  const registerThis = async () => {
    setMsg(null);
    try {
      const body = { platform: 'web', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, locale: navigator.language, app_version: 'console-0.1.0', model: navigator.userAgent.slice(0, 60) };
      const { status } = await api<Device>(`/api/me/devices/${getDeviceId()}`, { method: 'PUT', body });
      setMsg(status === 201 ? '已注册当前浏览器为设备' : '已刷新当前设备');
      await load();
    } catch (err) {
      setMsg(describeError(err));
    }
  };
  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">设备 <span className="text-xs text-zinc-400">/api/me/devices</span></h2>
        <div className="flex gap-2"><button className="btn-ghost" onClick={() => void load()}>刷新</button><button className="btn" onClick={() => void registerThis()}>注册当前浏览器</button></div>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {devices.map((d) => (
            <tr key={d.id} className="border-b border-zinc-800/60">
              <td className="py-1 font-mono">{d.id === getDeviceId() ? '★ ' : ''}{d.id.slice(0, 18)}…</td>
              <td>{d.platform}</td>
              <td>{d.timezone}{d.locale ? ` · ${d.locale}` : ''}</td>
              <td className="font-mono text-zinc-400">{d.push_token_suffix ? `…${d.push_token_suffix}` : '无 token'}</td>
              <td className="text-zinc-400">{fmt(d.last_seen_at)}</td>
              <td><button className="btn-ghost py-0" onClick={() => void (async () => { try { await api(`/api/me/devices/${d.id}`, { method: 'DELETE' }); await load(); } catch (err) { setMsg(describeError(err)); } })()}>删除</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {!devices.length && <p className="text-xs text-zinc-500">还没有设备。</p>}
      {msg && <p className="mt-2 text-xs text-zinc-300">{msg}</p>}
    </section>
  );
}

function SessionsCard() {
  const { reloadMe } = useSession();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => { try { setSessions((await api<Session[]>('/api/me/sessions')).data); } catch (err) { setMsg(describeError(err)); } }, []);
  useEffect(() => { void load(); }, [load]);
  return (
    <section className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold">会话 <span className="text-xs text-zinc-400">/api/me/sessions</span></h2>
        <button className="btn-ghost" onClick={() => void load()}>刷新</button>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {sessions.map((s) => (
            <tr key={s.sid} className="border-b border-zinc-800/60">
              <td className="py-1 font-mono">{s.current ? '★ ' : ''}{s.sid.slice(0, 8)}…</td>
              <td className="font-mono text-zinc-400">{s.device_id ? `${s.device_id.slice(0, 16)}…` : '—'}</td>
              <td className="max-w-[180px] truncate text-zinc-400" title={s.user_agent ?? ''}>{s.user_agent ?? '—'}</td>
              <td className="text-zinc-400">{fmt(s.rotated_at)}</td>
              <td><button className="btn-ghost py-0" onClick={() => void (async () => { try { await api(`/api/me/sessions/${s.sid}`, { method: 'DELETE' }); if (s.current) { setTokens(null); await reloadMe(); } else await load(); } catch (err) { setMsg(describeError(err)); } })()}>{s.current ? '撕除（=退出当前）' : '撕除'}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {msg && <p className="mt-2 text-xs text-zinc-300">{msg}</p>}
    </section>
  );
}

function DeleteAccountCard({ me }: { me: Me }) {
  const { reloadMe } = useSession();
  const [msg, setMsg] = useState<string | null>(null);
  if (me.status === 'pending_deletion') return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!confirm('确认申请注销？30 天宽限期内可登录取消。剩余钻石不退款。')) return;
    setMsg(null);
    try {
      const { data } = await api<{ purge_after: string }>('/api/me/delete', { method: 'POST', body: {} });
      setMsg(`已进入宽限期，计划清理时间 ${fmt(data.purge_after)}；当前会话已撕除，重新登录可取消。`);
      setTokens(null);
      await reloadMe();
    } catch (err) {
      setMsg(describeError(err));
    }
  };
  return (
    <section className="card border border-red-900/50">
      <h2 className="mb-3 font-semibold text-red-200">注销账号 <span className="text-xs text-zinc-400">POST /api/me/delete（已登录即可）</span></h2>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <button className="btn bg-red-700 hover:bg-red-600">申请注销（30 天宽限）</button>
      </form>
      {msg && <p className="mt-3 text-xs text-zinc-300">{msg}</p>}
    </section>
  );
}

function Dot({ on }: { on?: boolean }) {
  return <span className={`inline-block h-2 w-2 rounded-full ${on ? 'bg-emerald-400' : 'bg-zinc-600'}`} />;
}
